import {observer} from "mobx-react";
import {
  IconButton,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {Frame} from "../common/Frame";
import {recordings} from "../../proto/compiled";
import {MobxUtils} from "../../utils/MobxUtils";

export interface SessionMetaFormData {
  title: string;
  description: string;
  participants: recordings.IRecordingParticipant[];
}

interface SessionMetaFormProps {
  data: SessionMetaFormData;
  onChange: (data: SessionMetaFormData) => void;
  disabled?: boolean;
}

export const SessionMetaForm = observer(({data, onChange, disabled = false}: SessionMetaFormProps) => {
  const updateField = <K extends keyof SessionMetaFormData>(field: K, value: SessionMetaFormData[K]) => {
    onChange({...data, [field]: value});
  };

  const addParticipant = () => {
    const newParticipant = MobxUtils.makeObservable({
      id: `temp-${Date.now()}`,
      name: "",
      role: recordings.RecordingParticipantRole.rpr_other,
    });
    updateField('participants', [...data.participants, newParticipant]);
  };

  const removeParticipant = (index: number) => {
    const updated = [...data.participants];
    updated.splice(index, 1);
    updateField('participants', updated);
  };

  const updateParticipant = (index: number, field: 'name' | 'role', value: string | recordings.RecordingParticipantRole) => {
    const updated = [...data.participants];
    updated[index] = {...updated[index], [field]: value};
    updateField('participants', updated);
  };

  return (
    <Frame direction="column" gap={2}>
      {/* Title */}
      <Frame direction="column" gap={1}>
        <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
          TITLE
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={data.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Enter session title"
          disabled={disabled}
        />
      </Frame>

      {/* Description */}
      <Frame direction="column" gap={1}>
        <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
          DESCRIPTION
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          maxRows={8}
          value={data.description}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="Enter session description"
          disabled={disabled}
        />
      </Frame>

      {/* Participants */}
      <Frame direction="column" gap={1.5}>
        <Frame row alignItems="center" justifyContent="space-between">
          <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
            PARTICIPANTS
          </Typography>
          <IconButton size="small" onClick={addParticipant} disabled={disabled}>
            <AddIcon fontSize="small"/>
          </IconButton>
        </Frame>

        <Frame direction="column" gap={1.5}>
          {data.participants.map((participant, index) => (
            <Frame key={participant.id || index} row gap={1} alignItems="center">
              <TextField
                size="small"
                value={participant.name ?? ""}
                onChange={(e) => updateParticipant(index, 'name', e.target.value)}
                placeholder="Name"
                style={{flex: 2}}
                disabled={disabled}
              />
              <FormControl size="small" style={{flex: 1}}>
                <Select
                  value={participant.role ?? recordings.RecordingParticipantRole.rpr_other}
                  onChange={(e) => updateParticipant(index, 'role', e.target.value as recordings.RecordingParticipantRole)}
                  disabled={disabled}
                >
                  <MenuItem value={recordings.RecordingParticipantRole.rpr_other}>Other</MenuItem>
                  <MenuItem value={recordings.RecordingParticipantRole.rpr_instructor}>Instructor</MenuItem>
                  <MenuItem value={recordings.RecordingParticipantRole.rpr_student}>Student</MenuItem>
                  <MenuItem value={recordings.RecordingParticipantRole.rpr_supervisor}>Supervisor</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => removeParticipant(index)}
                color="error"
                disabled={disabled}
              >
                <DeleteIcon fontSize="small"/>
              </IconButton>
            </Frame>
          ))}
          {data.participants.length === 0 && (
            <Typography variant="body2" fontStyle="italic">
              No participants added
            </Typography>
          )}
        </Frame>
      </Frame>
    </Frame>
  );
});
